import { useCallback, useEffect, useState } from 'react';
import { Gamepad2, Loader2, Lock, Mail, RefreshCw } from 'lucide-react';
import {
  api,
  clearToken,
  getStoredToken,
  storeToken,
  type AuthMode,
  type ConfigDto,
  type PlatformDto,
  type StatusDto,
} from './api';
import { RetroLibrary } from './retro/RetroLibrary';

const SELKIES_IFRAME_ALLOW =
  'autoplay; fullscreen; gamepad; microphone; camera; clipboard-read; clipboard-write; screen-wake-lock';

const DEFAULT_PLATFORM_ID = 'retro';

type View = 'home' | 'play';

export default function App() {
  const [token, setToken] = useState(getStoredToken());
  const [config, setConfig] = useState<ConfigDto | null>(null);
  const [status, setStatus] = useState<StatusDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [view, setView] = useState<View>('home');
  const [activePlatform, setActivePlatform] = useState<PlatformDto | null>(null);
  const [initialPlatformOpened, setInitialPlatformOpened] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [cfg, st] = await Promise.all([api.config(), api.status()]);
      setConfig(cfg);
      setStatus(st);
    } catch (err) {
      if (err instanceof Error && err.message === 'UNAUTHORIZED') {
        clearToken();
        setToken('');
        // Volta ao gate sem alarme — sessão antiga / token inválido após troca para OTP
        setError('');
      } else {
        setError('Não foi possível carregar os jogos. Tente de novo.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token && config?.accessRequired) return;
    void load();
    const timer = setInterval(() => void api.status().then(setStatus).catch(() => {}), 30_000);
    return () => clearInterval(timer);
  }, [token, config?.accessRequired, load]);

  useEffect(() => {
    void api
      .config()
      .then((cfg) => {
        setConfig(cfg);
        if (!cfg.accessRequired) void load();
      })
      .catch(() => setError('Servidor indisponível.'));
  }, [load]);

  /** Entrada directa no Salão do Tom — iframe a ecrã inteiro, sem barra do portal. */
  useEffect(() => {
    if (!config || initialPlatformOpened) return;
    const retro = config.platforms.find((p) => p.id === DEFAULT_PLATFORM_ID);
    if (!retro) return;
    setActivePlatform(retro);
    setView('play');
    setInitialPlatformOpened(true);
  }, [config, initialPlatformOpened]);

  const openPlatform = (platform: PlatformDto) => {
    setActivePlatform(platform);
    setView('play');
  };

  const handleExitPlay = () => {
    setView('home');
    setActivePlatform(null);
    setInitialPlatformOpened(false);
  };

  if (config?.accessRequired && !token) {
    return (
      <GateScreen
        authMode={config.authMode}
        onUnlock={(t) => {
          storeToken(t);
          setToken(t);
        }}
        error={error}
      />
    );
  }

  if (view === 'play' && activePlatform) {
    if (activePlatform.id === DEFAULT_PLATFORM_ID) {
      return <RetroLibrary onExit={handleExitPlay} />;
    }
    return (
      <PlayView
        platform={activePlatform}
        status={status?.platforms.find((p) => p.id === activePlatform.id)?.status ?? 'offline'}
        onExit={handleExitPlay}
      />
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <header className="flex items-center gap-3 px-5 py-4 sm:px-8">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-500/30 ring-1 ring-violet-300/30">
          <Gamepad2 className="h-6 w-6 text-violet-200" />
        </div>
        <div>
          <h1 className="text-xl font-extrabold tracking-tight text-white sm:text-2xl">Central de Jogos</h1>
          <p className="text-sm text-violet-200/80">Escolha um console e divirta-se!</p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="ml-auto rounded-xl bg-white/10 p-2.5 text-white/90 transition hover:bg-white/20"
          title="Atualizar"
          aria-label="Atualizar"
        >
          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </header>

      <main className="flex-1 px-4 pb-8 sm:px-8">
        {error ? (
          <p className="mb-4 rounded-2xl bg-red-500/20 px-4 py-3 text-center text-sm text-red-100">{error}</p>
        ) : null}

        {loading && !config ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-violet-300" />
          </div>
        ) : (
          <div className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(config?.platforms ?? []).map((platform) => {
              const online = status?.platforms.find((p) => p.id === platform.id)?.status === 'online';
              return (
                <PlatformCard
                  key={platform.id}
                  platform={platform}
                  online={online}
                  onPlay={() => openPlatform(platform)}
                />
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

function GateScreen({
  authMode,
  onUnlock,
  error,
}: {
  authMode: AuthMode;
  onUnlock: (token: string) => void;
  error: string;
}) {
  if (authMode === 'otp') {
    return <OtpGateScreen onUnlock={onUnlock} error={error} />;
  }
  return <TokenGateScreen onUnlock={onUnlock} error={error} />;
}

function OtpGateScreen({ onUnlock, error }: { onUnlock: (token: string) => void; error: string }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [localError, setLocalError] = useState('');

  const sendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setLocalError('');
    setMessage('');
    try {
      const data = await api.sendOtp(email.trim());
      setMessage(data.message || 'Código enviado.');
      setStep('code');
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Erro ao enviar código.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setLocalError('');
    try {
      const data = await api.verifyOtp(email.trim(), code.trim());
      storeToken(data.token);
      await api.status();
      onUnlock(data.token);
    } catch (err) {
      clearToken();
      setLocalError(err instanceof Error ? err.message : 'Código inválido.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <div className="w-full max-w-sm rounded-3xl bg-white/10 p-6 ring-1 ring-white/15 backdrop-blur-sm">
        <div className="mb-4 flex justify-center">
          <div className="rounded-2xl bg-violet-500/30 p-3">
            <Mail className="h-8 w-8 text-violet-200" />
          </div>
        </div>
        <h2 className="text-center text-lg font-bold text-white">Entrar na Central de Jogos</h2>
        <p className="mt-1 text-center text-sm text-violet-200/80">Receberá um código por e-mail</p>

        {step === 'email' ? (
          <form onSubmit={sendOtp} className="mt-4 space-y-3">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-xl border-0 bg-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-violet-400"
              placeholder="E-mail autorizado"
              required
              autoFocus
            />
            {localError || error ? (
              <p className="text-center text-sm text-red-300">{localError || error}</p>
            ) : null}
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full rounded-xl bg-violet-500 py-3 font-bold text-white transition hover:bg-violet-400 disabled:opacity-50"
            >
              {busy ? 'Enviando…' : 'Enviar código'}
            </button>
          </form>
        ) : (
          <form onSubmit={verify} className="mt-4 space-y-3">
            <input
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              className="w-full rounded-xl border-0 bg-white/10 px-4 py-3 text-center text-2xl tracking-[0.4em] text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-violet-400"
              placeholder="000000"
              required
              autoFocus
            />
            {message ? <p className="text-center text-sm text-violet-200/90">{message}</p> : null}
            {localError ? <p className="text-center text-sm text-red-300">{localError}</p> : null}
            <button
              type="submit"
              disabled={busy || code.length !== 6}
              className="w-full rounded-xl bg-violet-500 py-3 font-bold text-white transition hover:bg-violet-400 disabled:opacity-50"
            >
              {busy ? 'Verificando…' : 'Entrar'}
            </button>
            <button
              type="button"
              onClick={() => setStep('email')}
              className="w-full text-sm text-violet-300 hover:text-violet-200"
            >
              Voltar
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function TokenGateScreen({ onUnlock, error }: { onUnlock: (token: string) => void; error: string }) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setLocalError('');
    storeToken(value.trim());
    try {
      await api.status();
      onUnlock(value.trim());
    } catch {
      clearToken();
      setLocalError('Senha incorreta.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center px-4">
      <form
        onSubmit={submit}
        className="w-full max-w-sm rounded-3xl bg-white/10 p-6 ring-1 ring-white/15 backdrop-blur-sm"
      >
        <div className="mb-4 flex justify-center">
          <div className="rounded-2xl bg-violet-500/30 p-3">
            <Lock className="h-8 w-8 text-violet-200" />
          </div>
        </div>
        <h2 className="text-center text-lg font-bold text-white">Área protegida</h2>
        <p className="mt-1 text-center text-sm text-violet-200/80">Digite a senha para entrar</p>
        <input
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mt-4 w-full rounded-xl border-0 bg-white/10 px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-violet-400"
          placeholder="Senha"
          autoFocus
        />
        {localError || error ? (
          <p className="mt-2 text-center text-sm text-red-300">{localError || error}</p>
        ) : null}
        <button
          type="submit"
          disabled={busy || !value.trim()}
          className="mt-4 w-full rounded-xl bg-violet-500 py-3 font-bold text-white transition hover:bg-violet-400 disabled:opacity-50"
        >
          {busy ? 'Verificando…' : 'Entrar'}
        </button>
      </form>
    </div>
  );
}

function PlatformCard({
  platform,
  online,
  onPlay,
}: {
  platform: PlatformDto;
  online: boolean;
  onPlay: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onPlay}
      className="group flex flex-col rounded-3xl bg-white/10 p-5 text-left ring-1 ring-white/10 transition hover:-translate-y-0.5 hover:bg-white/15 hover:ring-violet-300/40"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-4xl" role="img" aria-hidden>
          {platform.emoji}
        </span>
        <StatusBadge online={online} />
      </div>
      <h3 className="mt-3 text-lg font-extrabold text-white">{platform.name}</h3>
      <p className="mt-1 flex-1 text-sm leading-relaxed text-violet-100/75">{platform.description}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-violet-300 group-hover:text-violet-200">
        Jogar →
      </span>
    </button>
  );
}

function StatusBadge({ online }: { online: boolean }) {
  return (
    <span
      className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${
        online ? 'bg-emerald-500/25 text-emerald-200' : 'bg-amber-500/25 text-amber-200'
      }`}
    >
      {online ? 'Online' : 'Offline'}
    </span>
  );
}

function PlayView({
  platform,
  status,
  onExit,
}: {
  platform: PlatformDto;
  status: 'online' | 'offline';
  onExit?: () => void;
}) {
  const token = getStoredToken();
  const iframeSrc = platform.playUrl;
  const src = token
    ? `${iframeSrc}${iframeSrc.includes('?') ? '&' : '?'}access_token=${encodeURIComponent(token)}`
    : iframeSrc;

  if (status === 'offline') {
    return (
      <div className="flex h-full min-h-screen flex-col items-center justify-center gap-3 bg-black px-4 text-center">
        <p className="text-lg font-bold text-amber-200">Serviço offline no momento</p>
        <p className="max-w-md text-sm text-violet-200/80">
          O emulador de {platform.name} ainda não está rodando no servidor. Peça para o papai iniciar o serviço.
        </p>
      </div>
    );
  }

  return (
    <iframe
      title={platform.name}
      src={src}
      className="fixed inset-0 block h-[100dvh] w-[100dvw] border-0 bg-black"
      allow={SELKIES_IFRAME_ALLOW}
    />
  );
}
